import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Insight {
  type: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  action: string;
  onClick: () => void;
}

interface InsightsPanelProps {
  readonly insights: Insight[];
  readonly isCollapsed?: boolean;
  readonly onToggleCollapse?: () => void;
}

export function InsightsPanel({ insights, isCollapsed = false, onToggleCollapse }: InsightsPanelProps): React.ReactElement {
  // Collapsed view - just a vertical bar with toggle button
  if (isCollapsed) {
    return (
      <div className="relative h-full bg-muted/30 border-l flex flex-col items-center py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-8 w-8"
          title="Expand Insights"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="mt-4 writing-mode-vertical text-sm font-semibold">
          <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            Insights
          </span>
        </div>
      </div>
    );
  }

  // Expanded view - full panel
  return (
    <div className="relative h-full bg-white dark:bg-black border-l flex flex-col">
      {/* Header with collapse button */}
      <div className="flex-shrink-0 border-b border-border/40 p-4 sticky top-0 bg-white dark:bg-black z-10">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Insights</h3>
          </div>

          {/* Close Button */}
          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className="h-7 w-7 text-muted-foreground hover:text-foreground flex-shrink-0"
              title="Close Insights"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          AI-powered compliance analysis
        </p>
      </div>

      {/* Content area with scroll */}
      <div className="flex-1 overflow-y-auto p-4">
        {insights.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">
              No insights available at this time
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {insights.map((insight) => {
              const Icon = insight.icon;
              const colorClasses = {
                critical: 'bg-background dark:bg-background hover:bg-red-50/50 dark:hover:bg-red-950/10 border-l-red-500',
                warning: 'bg-background dark:bg-background hover:bg-amber-50/50 dark:hover:bg-amber-950/10 border-l-amber-500',
                success: 'bg-background dark:bg-background hover:bg-emerald-50/50 dark:hover:bg-emerald-950/10 border-l-emerald-500',
                info: 'bg-background dark:bg-background hover:bg-blue-50/50 dark:hover:bg-blue-950/10 border-l-blue-500',
              };
              const iconColors = {
                critical: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30',
                warning: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30',
                success: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30',
                info: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30',
              };
              const actionColors = {
                critical: 'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300',
                warning: 'text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300',
                success: 'text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300',
                info: 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300',
              };

              return (
                <button
                  key={insight.title}
                  type="button"
                  className={cn(
                    "group relative w-full text-left rounded-lg border border-border/40 border-l-[3px] transition-all duration-200 cursor-pointer",
                    colorClasses[insight.type as keyof typeof colorClasses]
                  )}
                  onClick={insight.onClick}
                >
                  <div className="p-3">
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "p-2 rounded-md flex-shrink-0 transition-transform duration-200 group-hover:scale-110",
                        iconColors[insight.type as keyof typeof iconColors]
                      )}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1">
                        <h4 className="font-semibold text-xs leading-tight text-foreground">{insight.title}</h4>
                        <p className="text-xs text-muted-foreground leading-snug">{insight.description}</p>
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 text-xs font-medium mt-1 transition-colors",
                            actionColors[insight.type as keyof typeof actionColors]
                          )}
                        >
                          {insight.action}
                          <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
